import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useNavigationType, useParams } from "react-router";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Sparkles, Users, History } from "lucide-react";
import { useStore } from "../stores/rootStore";
import { Spinner } from "../components/ui/Spinner";
import { PlayableGridSkeleton } from "../components/playable/PlayableGridSkeleton";
import { EmptyState } from "../components/ui/EmptyState";
import { DistributionPreviewProvider } from "../components/explore/DistributionCard";
import { playableKind } from "../lib/playableKind";
import { ExploreShelves, RetryMessage } from "../components/explore/ExploreShelves";
import { AllTags } from "../components/explore/AllTags";
import { CatalogueGrid } from "../components/explore/CatalogueGrid";
import { CatalogueSort } from "../components/explore/CatalogueSort";
import { catalogueOrder } from "../lib/catalogueOrder";
import { CatalogueActions } from "../components/explore/CatalogueActions";
import { topicPath } from "../lib/topics";
import { usePageMeta } from "../hooks/usePageMeta";
import { discoverFeedTab, isPrivateDiscoverTab } from "../lib/discoverTabs";
import { DiscoverySidebar } from "../components/layout/DiscoverySidebar";
import { FollowingEmptyRecommendations, RecentlyPlayedEmptyRecommendations } from "../components/explore/DiscoverEmptyRecommendations";

// The initial page is not counted; scrolling may append seven more pages automatically.
const AUTO_LOAD_PAGE_LIMIT = 7;
const scrollPositions = new Map<string, number>();

const DiscoverPage = observer(() => {
	const { auth, discover, explore, ui } = useStore();
	const navigate = useNavigate();
	const location = useLocation();
	const navigationType = useNavigationType();
	const { t, i18n } = useTranslation();
	const { tag } = useParams();
	const locale = i18n.resolvedLanguage?.startsWith("zh") ? "zh" : "en";
	const params = new URLSearchParams(location.search);
	const topicParam = tag?.trim().toLowerCase() || null;
	const legacyTopic = params.get("topic")?.trim().toLowerCase() || null;
	const categoryId = topicParam ? null : params.get("category");
	const sectionId = params.get("section");
	const category = discover.tagSections.find((section) => section.id === categoryId);
	const isAllTags = location.pathname.replace(/\/+$/, "") === "/all/tags";
	const feedTab = !isAllTags && !topicParam && !categoryId
		? discoverFeedTab(params.get("tab"))
		: null;
	const member = auth.isLoggedIn && !auth.isGuest;
	const privateFeedGuest = isPrivateDiscoverTab(feedTab) && !member;
	const isExplore = !isAllTags && !topicParam && !categoryId && !feedTab;
	const themeIndex = discover.tagSections.findIndex((section) => categoryId ? section.id === categoryId
		: !!topicParam && ((section.headerTarget?.type === "topic" && section.headerTarget.tag.toLowerCase() === topicParam) ||
			section.title?.replace(/^[^\p{L}\p{N}]+/u, "").trim().toLowerCase() === topicParam));
	const sortable = !isAllTags && (!!topicParam || category?.layout === "GRID");
	const order = sortable ? catalogueOrder(params.get("sort")) : "featured";
	const userId = auth.user?.id ?? null;
	const [autoLoadPages, setAutoLoadPages] = useState(0);
	const autoLoadSentinelRef = useRef<HTMLDivElement>(null);
	const autoLoadingRef = useRef(false);
	const navRef = useRef<HTMLElement>(null);
	const sidebarRef = useRef<HTMLDivElement>(null);
	const contentRef = useRef<HTMLDivElement>(null);
	const restoredKey = useRef<string | null>(null);
	const autoLoadKey = [userId ?? "guest", locale, topicParam, categoryId, feedTab, isAllTags, order].join(":");
	const currentAutoLoadKeyRef = useRef(autoLoadKey);
	currentAutoLoadKeyRef.current = autoLoadKey;

	usePageMeta(
		isAllTags ? { title: t("meta.pageTitle", { page: t("discover.allTags") }), description: t("discover.allTagsDescription") } : topicParam
			? {
					title: t("meta.pageTitle", { page: "#" + topicParam }),
					description: t("meta.topicDescription", { topic: topicParam }),
				}
			: {},
	);

	useEffect(() => {
		if (legacyTopic) {
			const rest = new URLSearchParams(location.search);
			rest.delete("topic");
			const search = rest.toString();
			navigate(topicPath(legacyTopic) + (search ? "?" + search : ""), { replace: true });
		} else if (location.hash === "#about") navigate("/about", { replace: true });
		else if (location.hash === "#create") navigate("/create", { replace: true });
	}, [legacyTopic, location.hash, location.search, navigate]);

	useEffect(() => {
		if (!auth.initialized) return;
		if (isPrivateDiscoverTab(discover.tab) && discover.loadedViewerKey !== (userId ?? "anonymous")) discover.reset();
		void discover.loadTagSections(false, locale, userId);
		void explore.ensureLoaded(locale, userId);
	}, [auth.initialized, locale, userId, discover, explore, isExplore, explore.historyVersion]);

	useEffect(() => {
		if (isExplore || isAllTags || !auth.initialized || legacyTopic) return;
		if (feedTab) {
			void discover.loadTab(feedTab, privateFeedGuest ? null : userId);
			return;
		}
		else if (topicParam) discover.setTopic(topicParam, order);
		else if (!discover.tagSectionsSettled) return;
		else if (category) discover.setTagSection(category.id, order);
		else if (!discover.tagSectionsError) {
			navigate("/", { replace: true });
			return;
		}
		void discover.ensureLoaded(userId);
	}, [
		auth.initialized,
		userId,
		locale,
		topicParam,
		order,
		category,
		isExplore,
		legacyTopic,
		isAllTags,
		feedTab,
		privateFeedGuest,
		discover,
		discover.tagSectionsSettled,
		discover.tagSectionsError,
		navigate,
	]);

	useEffect(() => {
		setAutoLoadPages(0);
		autoLoadingRef.current = false;
		const nav = window.matchMedia("(min-width: 768px)").matches ? sidebarRef.current : navRef.current;
		const selected = navRef.current?.querySelector('[aria-current="page"]');
		if (nav && selected) {
			const bounds = nav.getBoundingClientRect();
			const item = selected.getBoundingClientRect();
			nav.scrollBy({ left: item.left < bounds.left ? item.left - bounds.left : Math.max(0, item.right - bounds.right),
				top: item.top < bounds.top ? item.top - bounds.top : Math.max(0, item.bottom - bounds.bottom) });
		}
	}, [autoLoadKey]);
	useLayoutEffect(() => {
		if (restoredKey.current === location.key || !contentRef.current) return;
		const target = navigationType === "POP" ? scrollPositions.get(location.key) ?? 0 : 0;
		contentRef.current.scrollTo({ top: target, behavior: "instant" });
		// A returning route may still show a skeleton. Restore once its cards make room.
		if (contentRef.current.scrollHeight - contentRef.current.clientHeight >= target ||
			(!discover.loading && !explore.loading && !discover.meta.hasMore)) restoredKey.current = location.key;
	}, [location.key, navigationType, discover.loading, explore.loading, discover.items.length, explore.data]);

	useEffect(() => {
		if (
			(categoryId && !category) ||
			isExplore || isAllTags || privateFeedGuest ||
			autoLoadPages >= AUTO_LOAD_PAGE_LIMIT ||
			discover.loading ||
			discover.loadingMore ||
			!discover.meta.hasMore
		)
			return;
		const sentinel = autoLoadSentinelRef.current;
		if (!sentinel) return;
		if (!("IntersectionObserver" in window)) {
			setAutoLoadPages(AUTO_LOAD_PAGE_LIMIT);
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries.some((entry) => entry.isIntersecting) || autoLoadingRef.current) return;
				autoLoadingRef.current = true;
				const requestKey = autoLoadKey;
				void discover.loadMore().finally(() => {
					autoLoadingRef.current = false;
					if (currentAutoLoadKeyRef.current === requestKey)
						setAutoLoadPages((count) => Math.min(AUTO_LOAD_PAGE_LIMIT, count + 1));
				});
			},
			{ root: contentRef.current, rootMargin: "640px 0px" },
		);
		observer.observe(sentinel);
		return () => observer.disconnect();
	}, [
		isExplore,
		isAllTags,
		privateFeedGuest,
		categoryId,
		category,
		autoLoadKey,
		autoLoadPages,
		discover,
		discover.loading,
		discover.loadingMore,
		discover.meta.hasMore,
	]);

	const categoryPending = !!categoryId && !discover.tagSectionsSettled;
	const selectionPending =
		!isExplore && !isAllTags &&
		(feedTab ? discover.tab !== feedTab || discover.topic !== null || discover.tagSectionId !== null || discover.loadedViewerKey !== (userId ?? "anonymous")
			: topicParam ? discover.topic !== topicParam || discover.order !== order : discover.tagSectionId !== categoryId || discover.order !== order);

	return (
		<DistributionPreviewProvider>
			<div className="discover-distribution text-text" data-sidebar-collapsed={ui.discoveryCollapsed} data-sidebar-open={ui.discoveryMobileOpen}>
				<DiscoverySidebar isExplore={isExplore} feedTab={feedTab} categoryId={categoryId} topic={topicParam}
					navRef={navRef} scrollRef={sidebarRef} />
				<div ref={contentRef} className="discover-content" data-catalogue-scroll tabIndex={0} aria-label={t("discover.browseGames")}
					onScroll={() => {
						if (restoredKey.current !== location.key) return;
						scrollPositions.set(location.key, contentRef.current?.scrollTop ?? 0);
						if (scrollPositions.size > 100) scrollPositions.delete(scrollPositions.keys().next().value!);
					}}>
				<div className="discover-width space-y-7 pb-14 pt-6">
					{!isAllTags ? <div className={isExplore ? "sr-only" : "discover-heading"}>
						<div>
							<h1 className="text-2xl font-bold tracking-tight sm:text-[28px]">
								{isExplore
									? t("discover.explore")
									: topicParam
										? "#" + topicParam
										: feedTab ? t(`discover.${feedTab}`) : (category?.title ?? t("discover.explore"))}
							</h1>
							<p className="mt-1.5 text-sm text-muted">
								{isExplore
									? t("discover.exploreDescription")
									: feedTab === "recentlyPlayed" ? t("discover.recentlyPlayedDescription")
									: feedTab === "latest" ? t("discover.latestDescription")
									: feedTab === "following" ? t("discover.followingDescription")
									: feedTab === "popular" ? t("discover.popularDescription") : (category?.subtitle ?? t("discover.browseDescription"))}
							</p>
						</div>
						{sortable ? <CatalogueSort value={order} onChange={(value) => {
							const next = new URLSearchParams(location.search);
							value === "featured" ? next.delete("sort") : next.set("sort", value);
							navigate({ pathname: location.pathname, search: next.toString() });
						}} /> : null}
					</div> : null}
					{discover.tagSectionsError ? (
						<div className="flex items-center gap-3 text-sm text-muted">
							<span>{t("discover.categoriesFailed")}</span>
							<button
								type="button"
								className="underline underline-offset-4"
								onClick={() => void discover.loadTagSections(true, locale, userId)}
							>
								{t("common.retry")}
							</button>
						</div>
					) : null}
					{isAllTags ? <AllTags /> : privateFeedGuest ? (
						<EmptyState icon={feedTab === "recentlyPlayed" ? History : Users}
							title={t(feedTab === "recentlyPlayed" ? "discover.historySignInTitle" : "discover.followingSignInTitle")}
							description={t(feedTab === "recentlyPlayed" ? "discover.historySignInDescription" : "discover.followingSignInDescription")}
							action={<button type="button" className="btn-primary" onClick={() => ui.openAuth()}>{t("common.signIn")}</button>} />
					) : isExplore ? (
						<>
							{explore.error ? (
								<RetryMessage onRetry={() => void explore.ensureLoaded(locale, userId, true)} />
							) : null}
							<ExploreShelves sectionId={sectionId} />
						</>
					) : (
						<>
							{discover.error ? <RetryMessage onRetry={() => void discover.loadInitial()} /> : null}
							{selectionPending && discover.tagSectionsError ? null : !auth.initialized ||
							  categoryPending ||
							  discover.loading ||
							  selectionPending ? (
								<PlayableGridSkeleton className="discover-category-grid" />
							) : discover.error && discover.isEmpty ? null : discover.isEmpty && feedTab === "following" && member ? (
								<FollowingEmptyRecommendations key={userId + ":" + locale} viewerId={userId!} />
							) : discover.isEmpty && feedTab === "recentlyPlayed" && member ? (
								<RecentlyPlayedEmptyRecommendations key={userId + ":" + locale} />
							) : discover.isEmpty ? (
								<EmptyState
									icon={Sparkles}
									title={t(feedTab === "recentlyPlayed" ? "discover.historyEmptyTitle" : "discover.emptyTitle")}
									description={t(feedTab === "recentlyPlayed" ? "discover.historyEmptyDescription" : "discover.emptyDesc")}
								/>
							) : (
								<CatalogueGrid key={autoLoadKey} seed={categoryId ?? topicParam ?? feedTab ?? "catalogue"} items={discover.visibleItems}
									pattern={themeIndex >= 0 ? themeIndex : undefined} ordered={feedTab === "recentlyPlayed"}
									properties={(item, index) => ({ content_id: item.id, content_kind: playableKind(item), source: "discover",
										position: index + 1, feed_tab: discover.tab, category: discover.analyticsCategory })} />
							)}
							{!selectionPending && discover.meta.hasMore && autoLoadPages < AUTO_LOAD_PAGE_LIMIT ? (
								<div
									ref={autoLoadSentinelRef}
									className="grid h-14 place-items-center"
									aria-hidden="true"
								>
									{discover.loadingMore ? <Spinner size={18} /> : null}
								</div>
							) : !selectionPending && discover.meta.hasMore ? (
								<div className="flex justify-center">
									<button
										type="button"
										onClick={() => void discover.loadMore()}
										disabled={discover.loadingMore}
										className="btn-ghost"
									>
										{discover.loadingMore ? <Spinner size={15} /> : null}
										{t("common.loadMore")}
									</button>
								</div>
							) : null}
						</>
					)}
					<CatalogueActions key={location.pathname + location.search} scrollTarget={contentRef} />
				</div>
				</div>
			</div>
		</DistributionPreviewProvider>
	);
});

export default DiscoverPage;
